import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Combo, Switch } from '@fx-ui/fine-design';
import ShareUrlInput from '~/container/share-url-input';
import { get, getApi, insertURLQuery, isNil, parseDate, post, regionUtils, tracker } from '~/utils';
import Intl from '@fx-ui/intl';
import { API, TRACKER } from '~/constants';
import { useConfigProvider, useStores } from '~/hooks';
import { IDept } from '~/interfaces';
import { AxiosResponse } from 'axios';
import { RegionContext } from '~/contexts';

enum VALIDITY_DURATION {
    ONE_DAY = 1,
    THREE_DAYS = 3,
    ONE_WEEK = 7,
    ONE_MONTH = 30,
    ONE_YEAR = 365,
    PERMANENT = -1
}

interface IPublicLinkPanel {
    currentDeptInfo?: IDept;
}

interface IDeptConfigData {
    // 链接是否启用
    enabled?: boolean;
    // 邀请链接有效期
    invite_period?: VALIDITY_DURATION;
    // 邀请链接到期时间
    expire_time?: string;
}

/**
 * 企业成员公开链接邀请
 *
 * @param props IPublicLinkPanel
 * @param props.currentDeptInfo 当前选择的部门信息
 * @function
 */
const PublicLinkPanel: React.FC<IPublicLinkPanel> = ({ currentDeptInfo = {} }) => {
    const { dept_no } = currentDeptInfo;
    const [showLinkAddress, setShowLinkAddress] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(true);
    const [inviteLink, setInviteLink] = useState<string>('');
    // 有效期（下拉框中的选项值，是一个间隔时间，不是具体时间）
    const [validityDuration, setValidityDuration] = useState<VALIDITY_DURATION>(
        VALIDITY_DURATION.THREE_DAYS
    );
    // 到期时间
    const [expireTime, setExpireTime] = useState<string>('');
    // 是否邀请到指定部门
    const isInviteToDept = !isNil(dept_no);
    const { timezone } = useConfigProvider();
    const region = useContext(RegionContext);
    const { corpUserStore } = useStores();

    /**
     * 公开链接分为两种情况：1、邀请到指定部门；2、邀请到企业(不指定部门)
     * 两种情况的【邀请链接】都是从 corp_config_get 接口获取，只是部门邀请链接需要拼接dept_no参数
     * 两种情况链接是否启用是从不同接口获取
     */
    const getConfigData = () => {
        post(getApi(API.corp.get_corp_conf, corpUserStore.tenantId))
            .then((res) => {
                const resDate = get(res, 'data', {});
                if (isInviteToDept) {
                    setInviteLink(
                        insertURLQuery(resDate.invite_link, [
                            {
                                key: 'dept_no',
                                value: dept_no
                            }
                        ])
                    );
                } else {
                    // 只有没有指定部门的时候链接开启的状态才是从这个接口获取
                    setShowLinkAddress(resDate.has_link);
                    setInviteLink(resDate.invite_link);
                }
            })
            .finally(() => {
                setLoading(false);
            });
    };

    /**
     * 格式化后台返回的日期时间
     *
     * @param dateStr 日期时间字符串
     */
    const formatDateTime = (dateStr: string) => {
        return regionUtils.formatDatetime({
            value: parseDate(dateStr, timezone),
            region,
            formatType: 'ymd_hm'
        });
    };

    /**
     * 根据接口返回值设置state
     *
     * @param res 接口返回值
     */
    const setDeptConfigData = (res: AxiosResponse<IDeptConfigData>) => {
        const resDate: IDeptConfigData = get(res, 'data', {});
        setShowLinkAddress(!!resDate.enabled);
        setValidityDuration(resDate.invite_period || VALIDITY_DURATION.THREE_DAYS);
        setExpireTime(formatDateTime(resDate.expire_time || ''));
    };

    /**
     * 获取部门链接相关配置
     * 只是配置，不包含邀请链接本身，邀请链接是从corp_config_get接口获取再拼接上dept_no
     */
    const getDeptConfigData = () => {
        post(getApi(API.corp.invitation_get, corpUserStore.tenantId), { dept_no }).then(
            (res: AxiosResponse<IDeptConfigData>) => {
                setDeptConfigData(res);
            }
        );
    };

    /**
     * 设置指定部门邀请链接相关配置
     *
     * @param data 请求数据
     */
    const updateDeptConfigData = (data: Record<string, unknown>) => {
        post(getApi(API.corp.invitation_update, corpUserStore.tenantId), {
            enabled: showLinkAddress,
            dept_no: dept_no,
            invite_period: validityDuration,
            ...data
        }).then((res: AxiosResponse<IDeptConfigData>) => {
            setDeptConfigData(res);
        });
    };

    useEffect(() => {
        getConfigData();
        if (isInviteToDept) {
            getDeptConfigData();
        }
    }, []);

    const dealLinkShowSwitch = () => {
        const showLink = !showLinkAddress;
        if (isInviteToDept) {
            // 指定部门时调用特定接口
            updateDeptConfigData({ enabled: showLink });
        } else {
            post(getApi(API.corp.update_corp_conf, corpUserStore.tenantId), {
                has_link: showLink
            }).then(() => {
                setShowLinkAddress(showLink);
            });
        }
        tracker(TRACKER.MEMBER_LINK_SWITCH, {
            p1: showLink ? 'on' : 'off',
            p2: isInviteToDept ? 'department' : 'all'
        });
    };

    const renderShareUrl = () => {
        return (
            <ShareUrlInput
                className="link-input"
                hasQRCode
                downloadName={Intl.get('member.invite')}
                downloadUrl={inviteLink}
                onCopy={() => {
                    tracker(TRACKER.MEMBER_INVITE_LINK_CLICK);
                }}
                onQRCodeClick={() => {
                    tracker(TRACKER.MEMBER_INVITE_LINK_CLICK);
                }}
            />
        );
    };

    /**
     * 获取链接描述信息文案
     */
    const getLinkTip = () => {
        // 加入企业（未指定部门）
        if (!isInviteToDept) {
            // 加入企业，但是未启用链接
            if (!showLinkAddress) {
                return Intl.get('member.invite.link.tip.corp');
            }
            // 加入企业，启用了链接（加入企业的链接永久有效）
            return (
                <>
                    {Intl.get('member.invite.link.tip.corp.join')}
                    <span className="link-tip-mark">
                        {Intl.getHTML('member.invite.link.tip.permanent')}
                    </span>
                </>
            );
        }
        // 加入指定部门，但是没启用链接
        if (!showLinkAddress) {
            return Intl.get('member.invite.link.tip.dept');
        }
        // 加入指定部门，设置了连接有效期为【永久】
        if (validityDuration === VALIDITY_DURATION.PERMANENT) {
            return (
                <>
                    {Intl.get('member.invite.link.tip.dept.join')}
                    <span className="link-tip-mark">
                        {Intl.get('member.invite.link.tip.permanent')}
                    </span>
                </>
            );
        }
        // 加入指定部门，设置了连接有效期为【非永久日期】
        return (
            <>
                {Intl.get('member.invite.link.tip.dept.join')}
                <span className="link-tip-mark">
                    {Intl.get('member.invite.link.tip.dept.expire', { expireTime })}
                </span>
            </>
        );
    };

    const comboItems = useMemo(
        () => [
            {
                value: VALIDITY_DURATION.ONE_DAY,
                text: Intl.get('date.day', { day: VALIDITY_DURATION.ONE_DAY })
            },
            {
                value: VALIDITY_DURATION.THREE_DAYS,
                text: Intl.get('date.days', { day: VALIDITY_DURATION.THREE_DAYS })
            },
            {
                value: VALIDITY_DURATION.ONE_WEEK,
                text: Intl.get('date.days', { day: VALIDITY_DURATION.ONE_WEEK })
            },
            {
                value: VALIDITY_DURATION.ONE_MONTH,
                text: Intl.get('date.days', { day: VALIDITY_DURATION.ONE_MONTH })
            },
            {
                value: VALIDITY_DURATION.ONE_YEAR,
                text: Intl.get('date.days', { day: VALIDITY_DURATION.ONE_YEAR })
            },
            {
                value: VALIDITY_DURATION.PERMANENT,
                text: Intl.get('permanent')
            }
        ],
        []
    );

    const showCombo = () => showLinkAddress && inviteLink && isInviteToDept;

    return (
        <div className="public-link-panel">
            <div className="public-link-operate">
                <div className="operate-title">
                    {Intl.get('member.invite.link')}
                    {!loading && <span className="link-tip">{getLinkTip()}</span>}
                </div>
                {inviteLink && <Switch value={showLinkAddress} onChange={dealLinkShowSwitch} />}
                {showCombo() && (
                    <div className="content-line">
                        <p className="line-title">{Intl.get('member.invite.link.validity')}</p>
                        <Combo
                            className="validity-combo"
                            onChange={(value, item) => {
                                updateDeptConfigData({ invite_period: value });
                                tracker(TRACKER.MEMBER_LINK_INVITE_PERIOD, item?.text);
                            }}
                            value={validityDuration}
                            hasSearch={false}
                            hasClear={false}
                            items={comboItems}
                        />
                    </div>
                )}
            </div>
            {showLinkAddress && inviteLink && (
                <div className="public-link-address">
                    {Intl.get('link.address')}
                    {renderShareUrl()}
                </div>
            )}
        </div>
    );
};

export default PublicLinkPanel;
